import React, { ReactNode, useState } from 'react';
import { Button, Grid } from '@material-ui/core';
import {
  FormProvider,
  UseFormHandleSubmit,
  UseFormReturn,
  FieldErrors,
  UseFormWatch
} from 'react-hook-form';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { TSelectItem } from '../../types/common';
import { TCloudFileListForm } from '../../types/cloudStorage/CloudFileList';
import { AppDatePicker } from '../parts/input/AppDatePicker';
import { AppSelectfield } from '../parts/input/AppSelectfield';
import { AppTextfield } from '../parts/input/AppTextfield';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Accordion from 'react-bootstrap/Accordion';
import { useAccordionButton } from 'react-bootstrap/AccordionButton';
import 'bootstrap/dist/css/bootstrap.min.css';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { TransisionButton } from '../transitPictureOrFile/TransitionButton';

type Tprops = {
  methods: UseFormReturn<TCloudFileListForm, any>;
  selectItem: TSelectItem[];
  vendorsList: TSelectItem[];
  fileTypes: TSelectItem[];
  handleSubmit: UseFormHandleSubmit<TCloudFileListForm>;
  getFolderFileList: (mode?: string) => void;
  errors: FieldErrors<TCloudFileListForm>;
  getValues: any;
  reset: any;
  constructIdDef: TSelectItem;
  setButtonAreaFlg: React.Dispatch<React.SetStateAction<boolean>>;
  watch: UseFormWatch<TCloudFileListForm>;
};

/**
 * 検索欄
 * @constructor
 */
export const SearchField: React.FC<Tprops> = (props: Tprops) => {
  const {
    methods,
    selectItem,
    vendorsList,
    fileTypes,
    handleSubmit,
    getFolderFileList,
    errors,
    getValues,
    reset,
    constructIdDef,
    setButtonAreaFlg,
    watch
  } = props;

  // スタイル読み込み
  const classes = useStyles();

  // 日付validationのための終了日
  const [endDate, setEndDate] = useState('');
  const handleChangeEndDatePicker = (e: any) => {
    setEndDate(e.target.value);
  };

  const onSearch = async () => {
    const amountFrom = getValues('amount_from');
    const amountEnd = getValues('amount_end');

    if (amountFrom && amountEnd && parseInt(amountFrom) > parseInt(amountEnd)) {
      methods.setError('amount_end', {
        type: 'manual',
        message: '上限金額は下限金額より大きく設定してください。'
      });
      return;
    }

    await getFolderFileList('onSearch');
  };

  function ContextAwareToggle({
    children,
    eventKey,
    callback
  }: {
    children: ReactNode;
    eventKey: string;
    callback?: any;
  }) {
    const decoratedOnClick = useAccordionButton(
      eventKey,
      () => callback && callback(eventKey)
    );

    return (
      <div style={{ display: 'flex' }}>
        {children}
        <ExpandMoreIcon onClick={decoratedOnClick} />
      </div>
    );
  }

  const onChangeConstructId = async () => {
    const constructId = getValues('construct_id')?.value;
    if (constructId && constructId != 0) {
      setButtonAreaFlg(true);
    } else {
      setButtonAreaFlg(false);
    }

    await getFolderFileList('');
  };

  const onReset = async () => {
    reset({
      construct_id: getValues('construct_id'),
      folder_id: undefined,
      date_from: '',
      date_end: '',
      amount_from: '',
      amount_end: '',
      send_to: { value: 0, label: '--' },
      receive_from: { value: 0, label: '--' },
      keyword: '',
      file_type_id: { value: 0, label: '--' }
    });
  };

  return (
    <FormProvider {...methods}>
      <div className={classes.searchFieldMain}>
        <div style={{ display: 'flex' }}>
          <Grid container style={{ marginBottom: 15 }}>
            <Grid item xs={12} style={{ display: 'flex' }}>
              <AppSelectfield
                name='construct_id'
                label='案件'
                defaultValue={constructIdDef}
                listDatas={selectItem}
                onChange={onChangeConstructId}
                required={true}
                error={'construct_id' in errors}
                helperText={
                  errors?.construct_id
                    ? '必須です。'
                    : getValues('construct_id') &&
                      getValues('construct_id').value == 0 &&
                      'まず案件を選択してください。'
                }
                style={{ width: 400 }}
              />
            </Grid>
          </Grid>

          <TransisionButton
            watch={watch}
            linkName={'写真一覧'}
            path={'/pictures'}
          />
        </div>
        <Accordion className={classes.accordion}>
          <div>
            <div>
              <ContextAwareToggle eventKey='0'>
                <Heading>詳細で絞り込む</Heading>
              </ContextAwareToggle>
            </div>
            <Accordion.Collapse eventKey='0'>
              <div style={{ margin: '20px 0px', paddingBottom: '1px' }}>
                <div style={{ display: 'flex' }}>
                  <div className={classes.margin} style={{ width: 200 }}>
                    <AppDatePicker
                      name='date_from'
                      label='開始日'
                      toDate={endDate}
                      fullWidth={true}
                      errorMessage={errors?.date_from?.message}
                    />
                  </div>
                  <div className={classes.margin} style={{ width: 20 }}>
                    <span className={classes.textStyle}>〜</span>
                  </div>
                  <div className={classes.margin} style={{ width: 200 }}>
                    <AppDatePicker
                      name='date_end'
                      label='終了日'
                      fullWidth={true}
                      onChange={handleChangeEndDatePicker}
                    />
                  </div>
                  <div className={classes.margin} style={{ width: 20 }}></div>
                  <div className={classes.margin} style={{ width: 200 }}>
                    <AppTextfield
                      name='amount_from'
                      label='下限金額'
                      style={{ width: 180 }}
                      maxLength={14}
                    />
                    <span style={{ fontSize: 18, marginLeft: 2 }}>円</span>
                  </div>
                  <div className={classes.margin} style={{ width: 20 }}>
                    <span className={classes.textStyle}>〜</span>
                  </div>
                  <div className={classes.margin} style={{ width: 200 }}>
                    <AppTextfield
                      name='amount_end'
                      label='上限金額'
                      style={{ width: 180 }}
                      errorMessage={errors?.amount_end?.message}
                      maxLength={14}
                    />
                    <span style={{ fontSize: 18, marginLeft: 2 }}>円</span>
                  </div>
                </div>

                <div style={{ display: 'flex', marginTop: '20px' }}>
                  <div className={classes.margin} style={{ width: 200 }}>
                    <AppSelectfield
                      name='file_type_id'
                      label='資料種別'
                      defaultValue={{ value: 0, label: '--' }}
                      listDatas={fileTypes}
                      value={getValues('file_type_id')}
                      fullWidth={true}
                    />
                  </div>
                  <div className={classes.margin} style={{ width: 20 }}></div>
                  <div className={classes.margin} style={{ width: 200 }}>
                    <AppSelectfield
                      name='send_to'
                      label='送付先'
                      defaultValue={{ value: 0, label: '--' }}
                      listDatas={vendorsList}
                      value={getValues('send_to')}
                      fullWidth={true}
                    />
                  </div>
                  <div className={classes.margin} style={{ width: 20 }}></div>
                  <div className={classes.margin} style={{ width: 200 }}>
                    <AppSelectfield
                      name='receive_from'
                      label='受取元'
                      defaultValue={{ value: 0, label: '--' }}
                      listDatas={vendorsList}
                      value={getValues('receive_from')}
                      fullWidth={true}
                    />
                  </div>
                  <div className={classes.margin} style={{ width: 20 }}></div>
                  <div className={classes.margin} style={{ width: 200 }}>
                    <AppTextfield
                      name='keyword'
                      label='キーワード'
                      fullWidth={true}
                      maxLength={100}
                    />
                  </div>
                  <div className={classes.margin} style={{ width: 200 }}>
                    <Button
                      className={classes.searchBtn}
                      style={{ margin: '0px 5px' }}
                      variant='contained'
                      color='primary'
                      onClick={handleSubmit(onSearch)}
                    >
                      検索
                    </Button>
                    <input
                      type='button'
                      onClick={onReset}
                      value='条件リセット'
                      style={{
                        textDecoration: 'underline',
                        color: 'black',
                        background: 'transparent',
                        border: 'none',
                        outline: 'none'
                      }}
                    />
                  </div>
                </div>
              </div>
            </Accordion.Collapse>
          </div>
        </Accordion>
      </div>
    </FormProvider>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    searchFieldMain: {
      margin: '10px 0px',
      backgroundColor: 'white',
      padding: 10
    },
    searchField: {
      borderTop: '1px solid rgb(245, 245, 245)',
      borderBottom: '1px solid rgb(245, 245, 245)',
      // overflow: "hidden",
      padding: 10
    },
    searchBtn: {
      marginBottom: 10,
      marginRight: 20,
      marginLeft: 20,
      backgroundColor: '#52555F'
    },
    center: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    },
    textStyle: {
      fontSize: 18
    },
    margin: {
      margin: '0px 3px'
    },
    accordion: {
      margin: '10px 0px'
    }
  })
);

const Heading = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(15),
  fontWeight: theme.typography.fontWeightRegular
}));
